import React, { useState } from 'react';
import { StyledButton, AlertBox } from '../main/CommonStyles';
import styled from 'styled-components';
import apiClient from '../services/api';
import { KeyRound, Loader2, AlertTriangle, Clipboard, ClipboardCheck } from 'lucide-react';

const FormContainer = styled.div`
    padding: 1rem;
    background-color: #f8fafc;
    border-radius: 8px;
`;

const StyledForm = styled.form`
    display: flex;
    align-items: flex-end;
    gap: 1rem;
`;

const FormField = styled.div`
    flex-grow: 1;
    label {
        display: block;
        font-weight: 600;
        font-size: 0.9rem;
        margin-bottom: 0.5rem;
    }
    input {
        width: 100%;
        padding: 0.75rem;
        font-size: 1rem;
        border: 1px solid ${({ theme }) => theme.colors.borderColor};
        border-radius: ${({ theme }) => theme.borderRadius};
    }
`;

const ResultsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 1.5rem;

    th, td {
        padding: 0.5rem;
        text-align: left;
        border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
        font-size: 0.9rem;
    }
    th { background-color: ${({ theme }) => theme.colors.neutralLight}; }
    code {
        background-color: ${({ theme }) => theme.colors.primaryLightest};
        color: ${({ theme }) => theme.colors.primaryDarker};
        padding: 0.2rem 0.4rem;
        border-radius: 4px;
        font-weight: 700;
        font-family: 'Courier New', Courier, monospace;
    }
`;

const CopyButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.primary};
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem;
    border-radius: 4px;

    &:hover {
        background-color: ${({ theme }) => theme.colors.primaryLightest};
    }
`;

const GenerateCodes = ({ groupId }) => {
    const [numberOfCodes, setNumberOfCodes] = useState(10);
    const [generatedCodes, setGeneratedCodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [copiedCode, setCopiedCode] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setGeneratedCodes([]);
        try {
            const response = await apiClient.post(`/admin/groups/${groupId}/generate-codes`, {
                numberOfCodes: parseInt(numberOfCodes, 10),
            });
            setGeneratedCodes(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Wystąpił nieoczekiwany błąd.');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text, code) => {
        navigator.clipboard.writeText(text);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 1500);
    };

    return (
        <div>
            <FormContainer>
                <StyledForm onSubmit={handleSubmit}>
                    <FormField>
                        <label htmlFor={`numberOfCodes-${groupId}`}>Liczba kodów (1-100):</label>
                        <input
                            type="number"
                            id={`numberOfCodes-${groupId}`}
                            value={numberOfCodes}
                            onChange={(e) => setNumberOfCodes(e.target.value)}
                            min="1"
                            max="100"
                            required
                        />
                    </FormField>
                    <StyledButton type="submit" disabled={loading}>
                        {loading ? <Loader2 className="icon-spin" /> : <KeyRound />}
                        Generuj
                    </StyledButton>
                </StyledForm>
            </FormContainer>

            {error && <AlertBox type="error" style={{marginTop: '1rem'}}><AlertTriangle /> {error}</AlertBox>}

            {generatedCodes.length > 0 && (
                <div>
                    <ResultsTable>
                        <thead>
                        <tr>
                            <th>Nazwa Użytkownika</th>
                            <th>Kod Dostępu</th>
                            <th>Akcja</th>
                        </tr>
                        </thead>
                        <tbody>
                        {generatedCodes.map((item) => (
                            <tr key={item.code}>
                                <td>{item.username}</td>
                                <td><code>{item.code}</code></td>
                                <td>
                                    <CopyButton onClick={() => copyToClipboard(item.code, item.code)}>
                                        {copiedCode === item.code ? <ClipboardCheck size={16}/> : <Clipboard size={16}/>}
                                        {copiedCode === item.code ? 'Skopiowano!' : 'Kopiuj'}
                                    </CopyButton>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </ResultsTable>
                </div>
            )}
        </div>
    );
};

export default GenerateCodes;